import {
  Sidebar,
  SidebarDivider,
  SidebarGroup,
  SidebarItem,
  SidebarScrollWrapper,
  SidebarSpace,
  SidebarSubmenu,
  SidebarSubmenuItem,
} from '@backstage/core-components';
import type { NavContentComponentProps } from '@backstage/plugin-app-react';
import { NavContentBlueprint } from '@backstage/plugin-app-react';
import { SidebarLogo } from './SidebarLogo';
import AccountTreeIcon from '@material-ui/icons/AccountTree';
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline';
import BubbleChartIcon from '@material-ui/icons/BubbleChart';
import DomainIcon from '@material-ui/icons/Domain';
import GavelIcon from '@material-ui/icons/Gavel';
import MenuBookIcon from '@material-ui/icons/MenuBook';
import MenuIcon from '@material-ui/icons/Menu';
import SearchIcon from '@material-ui/icons/Search';
import TrackChangesIcon from '@material-ui/icons/TrackChanges';
import ViewModuleIcon from '@material-ui/icons/ViewModule';
import { SidebarSearchModal } from '@backstage/plugin-search';
import { UserSettingsSignInAvatar } from '@backstage/plugin-user-settings';
import { NotificationsSidebarItem } from '@backstage/plugin-notifications';

/**
 * Barra lateral organizada por intención: buscar, navegar el menú principal
 * (con la sección de arquitectura agrupada), crear/registrar y ajustes.
 *
 * Las entradas de página se toman con `nav.take(id)` usando el id del nodo
 * página; si el plugin no está instalado `take` devuelve `null` y no rompe.
 * Las entradas no incluidas explícitamente se añaden al final de "Menú" con
 * `nav.rest`.
 */
export function AppSidebar({
  navItems,
}: Pick<NavContentComponentProps, 'navItems'>) {
  const nav = navItems.withComponent(item => (
    <SidebarItem icon={() => item.icon} to={item.href} text={item.title} />
  ));

  // Elementos ocultos: se usa el modal de búsqueda y el item de notificaciones propio
  nav.take('page:search');
  nav.take('page:notifications');

  return (
    <Sidebar>
      {/* Logotipo de la aplicación */}
      <SidebarLogo />

      {/* Grupo de búsqueda */}
      <SidebarGroup label="Buscar" icon={<SearchIcon />} to="/search">
        <SidebarSearchModal />
      </SidebarGroup>
      <SidebarDivider />

      {/* Menú principal */}
      <SidebarGroup label="Menú" icon={<MenuIcon />}>
        {/* Navegación principal */}
        {nav.take('page:home')}
        {nav.take('page:catalog')}
        {nav.take('page:api-docs')}
        {nav.take('page:techdocs')}
        <SidebarDivider />

        {/* Arquitectura: agrupa decisiones, radar, dominios, sistemas y guías */}
        <SidebarItem icon={AccountTreeIcon} text="Arquitectura">
          <SidebarSubmenu title="Arquitectura">
            <SidebarSubmenuItem
              title="Decisiones (ADR)"
              to="/decisiones"
              icon={GavelIcon}
            />
            <SidebarSubmenuItem
              title="Tech Radar"
              to="/tech-radar"
              icon={TrackChangesIcon}
            />
            <SidebarSubmenuItem
              title="Dominios"
              to="/catalog?filters[kind]=domain"
              icon={DomainIcon}
            />
            <SidebarSubmenuItem
              title="Sistemas"
              to="/catalog?filters[kind]=system"
              icon={ViewModuleIcon}
            />
            <SidebarSubmenuItem
              title="Grafo de relaciones"
              to="/catalog-graph"
              icon={BubbleChartIcon}
            />
            <SidebarSubmenuItem
              title="Guía de arquitectura"
              to="/docs/default/component/backstage-devcore"
              icon={MenuBookIcon}
            />
          </SidebarSubmenu>
        </SidebarItem>

        {/* Creación y registro de componentes */}
        {nav.take('page:scaffolder')}
        <SidebarItem
          icon={AddCircleOutlineIcon}
          to="/catalog-import"
          text="Registrar existente"
        />

        {/* Anuncios (solo si el plugin está instalado) */}
        {nav.take('page:announcements')}

        {/* Consumido aquí para que no aparezca duplicado en "rest" */}
        {nav.take('page:tech-radar')}

        {/* Resto de páginas no clasificadas, ordenadas por título */}
        <SidebarScrollWrapper>
          {nav.rest({ sortBy: 'title' })}
        </SidebarScrollWrapper>
      </SidebarGroup>

      <SidebarSpace />
      <SidebarDivider />

      {/* Notificaciones */}
      <NotificationsSidebarItem />

      {/* Ajustes */}
      <SidebarGroup
        label="Ajustes"
        icon={<UserSettingsSignInAvatar />}
        to="/settings"
      >
        {nav.take('page:app-visualizer')}
        {nav.take('page:user-settings')}
      </SidebarGroup>
    </Sidebar>
  );
}

export const SidebarContent = NavContentBlueprint.make({
  params: {
    component: AppSidebar,
  },
});
